/**
 * Qué muestra exacta tiene delante el Laboratorio, y cuál verificó el servidor.
 *
 * ## El problema
 *
 * El 07/10/2026 se compararon dos corridas del Laboratorio sobre el mismo
 * vehículo: una con 7.079 registros y otra con 7.116, de períodos distintos. Con
 * eso no se puede decir si una diferencia de conteos viene del cálculo, de los
 * umbrales o de la entrada, porque la pantalla no decía qué entrada era.
 *
 * Y al guardar pasa lo mismo por dentro: la guarda de alcance de vehículo vuelve
 * a pedir la telemetría a WOX para no creerle al navegador. Si entre la carga y
 * el guardado WOX devuelve otros registros —llegan tarde, o el período es otro—,
 * el veto se decide sobre una muestra que el analista no vio, y nada lo decía.
 *
 * ## Lo que hace
 *
 * Identifica una muestra: equipo, período con su zona horaria, cuántos
 * registros, la huella de entrada (`computeInputDigest`, la misma que firma los
 * informes) y la versión del motor. El Laboratorio la muestra al cargar; el
 * servidor la calcula de nuevo al guardar.
 *
 * ## Dos huellas que no son lo mismo
 *
 * - **`inputDigest`, la huella de los registros.** Cubre solo los registros
 *   (sin `ingested_at`). Dos corridas con la misma huella leyeron los mismos
 *   registros; nada más.
 * - **La firma de la corrida (`computeAnalysisSignature`).** Es la identidad
 *   completa de una ejecución: equipo, período, zona, huella de entrada, huella
 *   del perfil, motor y pipeline. Dos pantallas con la misma firma calcularon lo
 *   mismo. La pantalla la muestra al lado de la huella de los registros.
 *
 * ## Qué compara `matchesLab`, qué se guarda y qué pasa si cambia
 *
 * - **Compara solo `inputDigest`:** la de la muestra que mostraba el
 *   Laboratorio contra la de la que vuelve a leer el servidor al guardar, para
 *   el mismo período declarado (`calibratedFromIso` / `calibratedToIso`). No
 *   compara el perfil, que es justo lo que se guarda, ni el motor, que es el del
 *   servidor.
 * - **No se persiste.** `calibrated_profile` guarda el período, los tramos, las
 *   notas, el perfil y su huella. Ni la huella de entrada ni `matchesLab`
 *   quedan en la base ni en la auditoría: viajan en la respuesta del guardado y
 *   en el texto del rechazo.
 * - **Si cambia, hoy se guarda igual y se dice.** No es una protección: una
 *   muestra distinta no impide guardar. Bloquear es la decisión D3, preparada
 *   aquí como `SAMPLE_MISMATCH_POLICY` y sin activar.
 */

import type { LabSampleIdentity } from "../contracts/api.ts";
import type { GuardiaHistoryRecord } from "../contracts/telemetry.ts";
import { DRIVING_ANALYSIS_VERSION, PIPELINE_VERSION } from "../pipeline/driving-analysis-v5.ts";
import { computeInputDigest } from "../pipeline/signature.ts";

export async function identifySample(
  records: GuardiaHistoryRecord[],
  input: { deviceId: string; fromIso: string; toIso: string; timezone: string; received: number },
): Promise<LabSampleIdentity> {
  return {
    deviceId: input.deviceId,
    fromIso: input.fromIso,
    toIso: input.toIso,
    timezone: input.timezone,
    records: records.length,
    received: input.received,
    inputDigest: await computeInputDigest(records),
    engineVersion: DRIVING_ANALYSIS_VERSION,
    pipelineVersion: PIPELINE_VERSION,
  };
}

/** Los doce primeros caracteres: bastan para comparar a ojo dos pantallas. */
export const shortDigest = (digest: string) => digest.slice(0, 12);

export type VerifiedSample = {
  sample: LabSampleIdentity;
  /**
   * `true` si es la muestra que mostraba el Laboratorio, `false` si no, `null`
   * si la solicitud no traía huella (un cliente anterior a este cambio).
   */
  matchesLab: boolean | null;
  /** La frase que se añade al rechazo, para que el analista no tenga que adivinar. */
  text: string;
};

/** Compara la muestra que verificó el servidor con la que mostraba el Laboratorio. */
export function compareWithLab(verified: LabSampleIdentity, shownDigest: string | null | undefined): VerifiedSample {
  const base =
    `Muestra que verificó el servidor: ${verified.records.toLocaleString("es-CO")} registros, ` +
    `huella ${shortDigest(verified.inputDigest)}, del ${verified.fromIso} al ${verified.toIso} ` +
    `(${verified.timezone}), motor ${verified.engineVersion}.`;

  if (!shownDigest) return { sample: verified, matchesLab: null, text: base };

  if (shownDigest === verified.inputDigest) {
    return { sample: verified, matchesLab: true, text: `${base} Es la misma que mostraba el Laboratorio.` };
  }

  return {
    sample: verified,
    matchesLab: false,
    text:
      `${base} No es la que mostraba el Laboratorio (huella ${shortDigest(shownDigest)}): entre la carga y el ` +
      `guardado cambió lo que devuelve la telemetría para ese período, así que los percentiles de la pantalla ` +
      `no son los del veto. Vuelve a cargar el recorrido antes de comparar cifras.`,
  };
}

/**
 * Qué hacer cuando la muestra que verifica el servidor no es la que mostraba el
 * Laboratorio (decisión D3 del ADR-013).
 *
 * - `INFORMAR` (vigente): se guarda igual y la respuesta lo dice
 *   (`verifiedSample.matchesLab: false`).
 * - `BLOQUEAR` (candidata): no se guarda y se pide volver a cargar, salvo que
 *   la huella coincida. Una huella ausente (`matchesLab === null`) también
 *   bloquea: sin huella no se puede comprobar que es la misma muestra, y dejarla
 *   pasar sería una vía para saltarse la regla.
 *
 * Bloquear tiene un costo: si la telemetría sigue recibiendo registros tardíos
 * de un período ya cerrado, cada carga queda vieja antes de guardar.
 */
export type SampleMismatchPolicy = "INFORMAR" | "BLOQUEAR";

/** Política vigente. **No cambia sin el visto bueno de Fabián** (ADR-013, D3). */
export const SAMPLE_MISMATCH_POLICY: SampleMismatchPolicy = "INFORMAR";

/** ¿Esta comparación impide guardar con la política dada? */
export function sampleMismatchBlocks(
  verified: VerifiedSample,
  policy: SampleMismatchPolicy = SAMPLE_MISMATCH_POLICY,
): boolean {
  return policy === "BLOQUEAR" && verified.matchesLab !== true;
}

/** El motivo del bloqueo D3, distinto para huella ausente y para huella distinta. */
export function sampleMismatchMessage(verified: VerifiedSample): string {
  const motivo =
    verified.matchesLab === null
      ? "la solicitud no trae la huella de la muestra que mostraba el Laboratorio, así que no se puede comprobar " +
        "que el servidor verificó la misma"
      : "la muestra cambió entre la carga y el guardado";
  return (
    `La calibración no se guardó porque ${motivo}. Vuelve a cargar la muestra y revisa las cifras antes de ` +
    `guardar. ${verified.text}`
  );
}
